import SmartToyOutlinedIcon from '@mui/icons-material/SmartToyOutlined';
import {
  Alert,
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  useTheme,
} from '@mui/material';
import { useIntl } from 'react-intl';

import { LoadingSpinner } from '@verantos-dev/shared-components';

import type { AgentAllocationPreviewStatus } from '../../hooks/useAnnotationAgentAllocation';
import type { AnnotationUser } from '../../state/createProjectSlice';
import type { AnnotationAgentAllocationPreviewResponse } from '../../types/response';

// TODO: cleanup with cp_annotation_createproject_agent ff

interface AgentAllocationSummaryTableProps {
  status: AgentAllocationPreviewStatus;
  preview: AnnotationAgentAllocationPreviewResponse | null;
  annotators: AnnotationUser[];
  /** Custom mode: a human's row shows "{pct}% · {count} encounters". */
  isCustom: boolean;
}

/**
 * The Review allocation summary while the annotation agent is on: one "All batches" group with
 * the agent first, then each human's share, and the 2n total. `AllocationSummaryTable` stays
 * the per-split table used without the agent.
 */
export const AgentAllocationSummaryTable = ({
  status,
  preview,
  annotators,
  isCustom,
}: AgentAllocationSummaryTableProps) => {
  const theme = useTheme();
  const { formatMessage } = useIntl();
  const title = (
    <Typography variant="h6">
      {formatMessage({ id: 'createProjectWizard.step.reviewAndCreate.allocationSummary.title' })}
    </Typography>
  );

  if (status === 'loading') {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(3) }}>
        {title}
        <Box
          role="status"
          aria-label={formatMessage({ id: 'createProjectWizard.step.allocation.agent.loading' })}
        >
          <LoadingSpinner id="agentAllocationSummary" />
        </Box>
      </Box>
    );
  }
  if (status === 'error' || !preview) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(3) }}>
        {title}
        <Alert severity="error">
          {formatMessage({ id: 'createProjectWizard.step.allocation.agent.error' })}
        </Alert>
      </Box>
    );
  }

  const nameById = new Map(annotators.map((annotator) => [annotator.id, annotator.label]));
  const rows = [
    {
      id: preview.agent.id,
      name: formatMessage({ id: 'createProjectWizard.step.allocation.agent.label' }),
      allocation: preview.agent.encounterCount.toLocaleString(),
      isAgent: true,
    },
    ...preview.humans.map((human) => ({
      id: human.id,
      name: nameById.get(human.id) ?? human.id,
      allocation:
        isCustom && human.sharePercentage !== null
          ? formatMessage(
              {
                id: 'createProjectWizard.step.reviewAndCreate.agent.allocationSummary.customShare',
              },
              { sharePercentage: human.sharePercentage, encounterCount: human.encounterCount }
            )
          : human.encounterCount.toLocaleString(),
      isAgent: false,
    })),
  ];

  return (
    <Box
      id="agent-allocation-summary-table"
      sx={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(3) }}
    >
      {title}
      <Paper variant="outlined">
        <Table>
          <TableHead>
            <TableRow
              sx={{
                '&:hover': { backgroundColor: 'transparent' },
                borderBottom: `2px solid ${theme.palette.divider}`,
                backgroundColor: theme.palette.grey[100],
              }}
            >
              <TableCell>
                {formatMessage({
                  id: 'createProjectWizard.step.reviewAndCreate.agent.allocationSummary.batch',
                })}
              </TableCell>
              <TableCell>
                {formatMessage({
                  id: 'createProjectWizard.step.reviewAndCreate.agent.allocationSummary.annotator',
                })}
              </TableCell>
              <TableCell align="right">
                {formatMessage({
                  id: 'createProjectWizard.step.reviewAndCreate.agent.allocationSummary.encounters',
                })}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row, index) => (
              <TableRow
                key={row.id}
                sx={{
                  '&:hover': { backgroundColor: 'transparent' },
                  borderBottom: `1px solid ${theme.palette.divider}`,
                }}
              >
                {index === 0 && (
                  <TableCell
                    rowSpan={rows.length}
                    sx={{ verticalAlign: 'top', fontWeight: theme.typography.fontWeightMedium }}
                  >
                    {formatMessage({
                      id: 'createProjectWizard.step.reviewAndCreate.agent.allocationSummary.allBatches',
                    })}
                  </TableCell>
                )}
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: theme.spacing(1) }}>
                    {row.isAgent && (
                      <SmartToyOutlinedIcon
                        fontSize="small"
                        sx={{ color: theme.palette.primary.main }}
                      />
                    )}
                    {row.name}
                  </Box>
                </TableCell>
                <TableCell align="right">{row.allocation}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>
      <Typography variant="body2" sx={{ fontWeight: theme.typography.fontWeightMedium }}>
        {formatMessage(
          { id: 'createProjectWizard.step.allocation.agent.totalAssignments' },
          { encounterCount: preview.encounterCount, assignmentCount: preview.encounterCount * 2 }
        )}
      </Typography>
    </Box>
  );
};
